import React, { useCallback, useEffect, useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { VOUCHER_NOTE_MAX_LENGTH } from '../../constant/constant';
import { authHeaders, formatReportDate } from '../../utils/reportFormat';

const StockGainLossReport = (props) => {
  const [gate, setGate] = useState(null);
  const [tanks, setTanks] = useState([]);
  const [nozzleGroups, setNozzleGroups] = useState([]);
  const [formulaRows, setFormulaRows] = useState([]);
  const [prevClosingDate, setPrevClosingDate] = useState('');
  const [approveButton, setApproveButton] = useState(0);
  const [physicalQty, setPhysicalQty] = useState({});
  const [gainLoss, setGainLoss] = useState({});
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);

  const parsePayload = async (response) => {
    const text = await response.text();
    if (!text || !text.trim()) {
      throw new Error('Empty response from server. Please refresh and try again.');
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error('Invalid server response. Please refresh and try again.');
    }

    if (!response.ok) {
      const err = new Error(
        data?.response?.messege
          || data?.response?.message
          || data?.message
          || `Unable to load stock gain/loss (HTTP ${response.status}).`
      );
      err.pendingTransactions = data?.response?.pending_transactions || null;
      err.transactions = data?.response?.transactions || null;
      err.blockedPayload = data?.response || data;
      throw err;
    }

    if (data?.response?.status === false) {
      const err = new Error(data.response.messege || data.response.message || 'Unable to load stock gain/loss.');
      err.pendingTransactions = data.response.pending_transactions || null;
      err.transactions = data.response.transactions || null;
      err.blockedPayload = data.response;
      throw err;
    }
    if (data.status === false) {
      throw new Error(data.message || 'Unable to load stock gain/loss.');
    }
    return data;
  };

  const tankKey = (id) => String(id);

  const applyNote = (payload, preserveNote = false) => {
    if (preserveNote || payload?.note === undefined) {
      return;
    }
    setNote(payload.note || '');
  };

  const loadTanks = useCallback(async (empDate, options = {}) => {
    const { preserveApproveButton = false, preserveNote = false } = options;
    const response = await fetch(`${API_URL}/stock_tank`, {
      method: 'post',
      headers: authHeaders(),
      body: JSON.stringify({ emp_date: empDate }),
    });
    const data = await parsePayload(response);
    setTanks(data.tank_data || []);
    if (!preserveApproveButton) {
      setApproveButton(data.approve_button ?? 0);
    }
    applyNote(data, preserveNote);

    const qtyMap = {};
    const gainMap = {};
    (data.tank_data || []).forEach((tank) => {
      const key = tankKey(tank.id);
      qtyMap[key] = tank.physical_quantity ?? '';
      gainMap[key] = tank.loss_gain_quantity ?? '';
    });
    setPhysicalQty(qtyMap);
    setGainLoss(gainMap);
  }, []);

  const mergeNozzleData = (payload) => {
    const groups = payload.gain_loss_data || [];
    const nozzleSets = payload.nozzle_datas || [];

    return groups.map((group, index) => ({
      ...group,
      nozzle_rows: nozzleSets[index] || [],
    }));
  };

  const loadNozzles = useCallback(async (empDate) => {
    const response = await fetch(`${API_URL}/nozzle_data`, {
      method: 'post',
      headers: authHeaders(),
      body: JSON.stringify({ emp_date: empDate }),
    });
    const data = await parsePayload(response);
    setNozzleGroups(mergeNozzleData(data));
    setFormulaRows(data.gain_loss_data || []);
    setPrevClosingDate(data.prev_emp_closing_date || '');
    setApproveButton(data.approve_button ?? 0);
    applyNote(data);
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/stock_gain`, { headers: authHeaders() });
        const data = await parsePayload(response);

        if (!data.emp_closing_date) {
          throw new Error('Operations not started, please start operation to view stock gain or loss.');
        }

        setGate({ blocked: false, empClosingDate: data.emp_closing_date });
        setApproveButton(data.approve_button ?? 0);
        applyNote(data);
        await loadTanks(data.emp_closing_date);
        await loadNozzles(data.emp_closing_date);
      } catch (error) {
        const pendingItems = Array.isArray(error.pendingTransactions)
          ? error.pendingTransactions
          : Array.isArray(error.transactions)
            ? error.transactions.map((label) => ({
              label,
              count: null,
              approve_path: '/day-closing',
            }))
            : [];

        if (pendingItems.length > 0) {
          setGate({
            blocked: true,
            pending: true,
            message: error.message || 'Please approve the pending transactions below.',
            pendingItems,
          });
        } else {
          setGate({ blocked: true, message: error.message });
        }
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [loadNozzles, loadTanks]);

  const updatePhysicalQty = (tankId, value) => {
    setPhysicalQty((prev) => ({ ...prev, [tankKey(tankId)]: value }));
  };

  const fetchGainLoss = async (tankId, value) => {
    if (value === '' || gate?.blocked) {
      setGainLoss((prev) => ({ ...prev, [tankKey(tankId)]: '' }));
      return;
    }

    const response = await fetch(`${API_URL}/get_gain_loss`, {
      method: 'post',
      headers: authHeaders(),
      body: JSON.stringify({ tank_id: tankId, physical_quantity: value }),
    });
    const data = await response.json();
    setGainLoss((prev) => ({ ...prev, [tankKey(tankId)]: data.gain_loss ?? '' }));
  };

  const saveTankDetails = async (tankId) => {
    const key = tankKey(tankId);
    const qty = physicalQty[key];
    if (qty === '' || qty === undefined) {
      swal('Please enter physical quantity', '', 'warning');
      return;
    }
    if (Number(qty) < 0) {
      swal('Physical quantity cannot be negative', '', 'warning');
      return;
    }
    if (Number(qty) > 50000) {
      swal('Physical quantity should not exceed 50,000 L', '', 'warning');
      return;
    }

    try {
      let gainLossValue = gainLoss[key];
      if (gainLossValue === '' || gainLossValue === undefined) {
        const gainResponse = await fetch(`${API_URL}/get_gain_loss`, {
          method: 'post',
          headers: authHeaders(),
          body: JSON.stringify({ tank_id: tankId, physical_quantity: qty }),
        });
        const gainData = await gainResponse.json();
        gainLossValue = gainData.gain_loss ?? '';
        setGainLoss((prev) => ({ ...prev, [key]: gainLossValue }));
      }

      const response = await fetch(`${API_URL}/view_detail`, {
        method: 'post',
        headers: authHeaders(),
        body: JSON.stringify({
          tank_id: tankId,
          physical_quantity: qty,
          gain_loss: gainLossValue,
          emp_date: gate.empClosingDate,
        }),
      });
      const data = await parsePayload(response);
      setApproveButton(data.approve_button ?? 0);
      setNozzleGroups(mergeNozzleData(data));
      setFormulaRows(data.gain_loss_data || []);
      setPrevClosingDate(data.prev_emp_closing_date || '');

      const qtyMap = { ...physicalQty };
      const gainMap = { ...gainLoss };
      (data.gain_loss_data || []).forEach((row) => {
        const rowKey = tankKey(row.tank_id);
        if (row.physical_quantity !== undefined && row.physical_quantity !== null) {
          qtyMap[rowKey] = row.physical_quantity;
        }
        if (row.loss_gain_quantity !== undefined && row.loss_gain_quantity !== null) {
          gainMap[rowKey] = row.loss_gain_quantity;
        }
      });
      setPhysicalQty(qtyMap);
      setGainLoss(gainMap);
      await loadTanks(gate.empClosingDate, { preserveApproveButton: true, preserveNote: true });
      swal('Saved', 'Tank details updated.', 'success');
    } catch (error) {
      swal('Stock Gain/Loss', error.message, 'error');
    }
  };

  const approveAll = async () => {
    if (approveButton !== 1) {
      return;
    }

    const confirmed = await swal({
      title: 'Approve stock gain/loss?',
      text: 'Once approved, entries cannot be edited again.',
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    });

    if (!confirmed) {
      return;
    }

    setApproving(true);
    try {
      const response = await fetch(`${API_URL}/approve`, {
        method: 'post',
        headers: authHeaders(),
        body: JSON.stringify({
          emp_date: gate.empClosingDate,
          note: note.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status === false) {
        throw new Error(data.message || 'Approval failed.');
      }

      await loadTanks(gate.empClosingDate);
      await loadNozzles(gate.empClosingDate);
      swal('Approved', data.message || 'Stock gain/loss approved.', data.mail_sent === false ? 'warning' : 'success');
    } catch (error) {
      swal('Stock Gain/Loss', error.message, 'error');
    } finally {
      setApproving(false);
    }
  };

  const isLocked = approveButton === 3;

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={3} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <div className="container pb-3">
            <h2 className="table-title text-center mb-3">Stock Gain/Loss</h2>

            {loading && <p className="text-muted">Loading…</p>}

            {!loading && gate?.blocked && (
              <div className="alert alert-warning">
                <div className="mb-2">{gate.message}</div>
                {gate.pending && Array.isArray(gate.pendingItems) && gate.pendingItems.length > 0 && (
                  <div className="table-responsive mt-2">
                    <table className="table table-sm table-bordered bg-white mb-2">
                      <thead>
                        <tr>
                          <th>Pending item</th>
                          <th>Count</th>
                          <th>Latest date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gate.pendingItems.map((item) => (
                          <tr key={`${item.ac_type || item.label}-${item.latest_date || 'pending'}`}>
                            <td>{item.label || item.ac_type || 'Pending transaction'}</td>
                            <td>{item.count != null ? item.count : '—'}</td>
                            <td>{item.latest_date || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="small mb-0">
                      Approve these pending items from their operational screens, then refresh this page.
                    </p>
                  </div>
                )}
              </div>
            )}

            {!loading && !gate?.blocked && (
              <>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label>Last employee closing date</label>
                    <input className="form-control" value={gate.empClosingDate || ''} disabled readOnly />
                  </div>
                  {(approveButton === 1 || (isLocked && note)) && (
                    <div className="col-md-4">
                      <label htmlFor="stock-gain-loss-note">Description</label>
                      <input
                        id="stock-gain-loss-note"
                        type="text"
                        className="form-control"
                        name="note"
                        value={note}
                        maxLength={VOUCHER_NOTE_MAX_LENGTH}
                        disabled={isLocked}
                        readOnly={isLocked}
                        placeholder="Stock Gain/Loss"
                        onChange={(e) => setNote(e.target.value)}
                      />
                      {!isLocked && (
                        <small className="text-muted">{String(note || '').length}/{VOUCHER_NOTE_MAX_LENGTH}</small>
                      )}
                    </div>
                  )}
                  <div className="col-md-4 d-flex align-items-end">
                    {approveButton === 1 && (
                      <button className="btn btn-success" onClick={approveAll} disabled={approving}>
                        {approving ? 'Approving…' : 'Approve'}
                      </button>
                    )}
                    {isLocked && <span className="badge bg-secondary ms-2">Approved</span>}
                  </div>
                </div>

                <div className="table-widget mb-4">
                  <table className="table border">
                    <thead>
                      <tr>
                        <th>Tank</th>
                        <th>Current stock (L)</th>
                        <th>Physical qty (L)</th>
                        <th>Gain/loss</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tanks.map((tank) => (
                        <tr key={tank.id}>
                          <td>{tank.tank_name}</td>
                          <td>{tank.current_stock}</td>
                          <td>
                            <input
                              type="text"
                              inputMode="decimal"
                              className="form-control"
                              disabled={isLocked}
                              value={physicalQty[tankKey(tank.id)] ?? ''}
                              onChange={(e) => updatePhysicalQty(tank.id, e.target.value)}
                              onBlur={(e) => fetchGainLoss(tank.id, e.target.value)}
                            />
                          </td>
                          <td>
                            <input className="form-control" value={gainLoss[tankKey(tank.id)] ?? ''} disabled readOnly />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={isLocked}
                              onClick={() => saveTankDetails(tank.id)}
                            >
                              View details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {formulaRows.length > 0 && (
                  <div className="table-widget mb-4">
                    <h5 className="mb-2">Stock gain/loss formula</h5>
                    <p className="text-muted small mb-3">
                      Logical quantity [2] = Opening stock + Purchase + Recoup − Sales [1].
                      Gain/loss [3] = Physical quantity − Logical quantity.
                      {prevClosingDate ? ` Previous adjustment date: ${formatReportDate(prevClosingDate)}.` : ''}
                    </p>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead>
                          <tr>
                            <th>Tank</th>
                            <th>Product</th>
                            <th>Opening stock</th>
                            <th>Purchase</th>
                            <th>Recoup</th>
                            <th>Logical qty [2]</th>
                            <th>Physical qty</th>
                            <th>Gain/loss [3]</th>
                            <th>Gain/loss</th>
                            <th>Avg price</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formulaRows.map((row) => (
                            <tr key={row.id || `${row.tank_id}-${row.op_date}`}>
                              <td>{row.tank_names || row.tank_name || '—'}</td>
                              <td>{row.fuel_name || '—'}</td>
                              <td>{row.opening_stock ?? '—'}</td>
                              <td>{row.purchase_quantity ?? '—'}</td>
                              <td>{row.recoup_quantity ?? '—'}</td>
                              <td>{row.logical_quantity ?? '—'}</td>
                              <td>{row.physical_quantity ?? '—'}</td>
                              <td>{row.loss_gain_quantity ?? '—'}</td>
                              <td>{row.loss_gain ?? '—'}</td>
                              <td>{row.avg_purchase_price ?? '—'}</td>
                              <td>{row.loss_gain_amount ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!formulaRows.length && !isLocked && (
                  <p className="text-muted">
                    Enter each tank&apos;s physical quantity and click <strong>View details</strong> to calculate the formula.
                  </p>
                )}

                {nozzleGroups.length > 0 && (
                  <div className="table-widget">
                    <h5 className="mb-3">Nozzle readings</h5>
                    {nozzleGroups.map((group) => (
                      <div key={group.id} className="mb-4">
                        <h6>{group.tank_names} ({group.fuel_name})</h6>
                        <table className="table table-sm border">
                          <thead>
                            <tr>
                              <th>Nozzle</th>
                              <th>Opening</th>
                              <th>Closing</th>
                              <th>Qty</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(group.nozzle_rows || []).map((nozzle, index) => (
                              <tr key={index}>
                                <td>{nozzle.nozzle_name}</td>
                                <td>{nozzle.nozzle_opening_reading}</td>
                                <td>{nozzle.nozzle_closing_reading}</td>
                                <td>{nozzle.quantity}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockGainLossReport;
